/**
 * `useNocStream` — un único consumidor SSE reutilizable (NOC y Laboratorio).
 *
 * Expone el modo de conexión y un buffer acotado de eventos recientes. Cada página
 * que lo usa monta su propia conexión (solo hay una ruta activa a la vez).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { requestSessionRefresh } from '@/lib/http/apiClient';
import { NOC_SSE_ENABLED } from '../api/nocApi';
import { consumeNocStream, type NocStreamEvent } from '../api/nocStream';

export type NocStreamMode = 'connecting' | 'open' | 'unavailable';

export interface NocStreamRecord {
  /** Secuencia local (monótona) para detectar el último evento sin comparar objetos. */
  seq: number;
  /** Nombre del evento SSE (`station.updated`, `sim.log`, ...). */
  type: string;
  /** `id:` del stream, si el backend lo emite (para `Last-Event-ID`). */
  streamId?: string;
  /** `at` del payload si existe; si no, la hora de recepción. */
  at: string;
  /** Payload JSON parseado (o el texto crudo si no era JSON). */
  data: unknown;
  receivedAt: number;
}

const MAX_EVENTS = 200;

export interface NocStreamState {
  mode: NocStreamMode;
  events: NocStreamRecord[];
  latest: NocStreamRecord | null;
  clear: () => void;
}

export function useNocStream(enabled: boolean = NOC_SSE_ENABLED): NocStreamState {
  const [mode, setMode] = useState<NocStreamMode>(enabled ? 'connecting' : 'unavailable');
  const [events, setEvents] = useState<NocStreamRecord[]>([]);
  const [latest, setLatest] = useState<NocStreamRecord | null>(null);
  const seqRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      setMode('unavailable');
      return;
    }
    const controller = new AbortController();
    setMode('connecting');
    void consumeNocStream({
      signal: controller.signal,
      onOpen: () => setMode('open'),
      onUnauthorized: async () => Boolean(await requestSessionRefresh()),
      onUnavailable: () => {
        setMode('unavailable');
        controller.abort();
      },
      onEvent: (event: NocStreamEvent) => {
        let parsed: unknown = event.data;
        try {
          parsed = JSON.parse(event.data) as unknown;
        } catch {
          // Evento no JSON (p. ej. keepalive): se conserva el texto crudo.
        }
        const at =
          typeof parsed === 'object' &&
          parsed !== null &&
          typeof (parsed as { at?: unknown }).at === 'string'
            ? (parsed as { at: string }).at
            : new Date().toISOString();
        seqRef.current += 1;
        const record: NocStreamRecord = {
          seq: seqRef.current,
          type: event.event,
          streamId: event.id,
          at,
          data: parsed,
          receivedAt: Date.now(),
        };
        setEvents((previous) => [...previous, record].slice(-MAX_EVENTS));
        setLatest(record);
      },
    });
    return () => {
      controller.abort();
    };
  }, [enabled]);

  const clear = useCallback(() => setEvents([]), []);

  return { mode, events, latest, clear };
}
